const SWIPE_THRESHOLD = 48;
const DIRECTION_LOCK_THRESHOLD = 8;
const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, label, summary, [role="button"]';

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(INTERACTIVE_SELECTOR));
}

type CarouselSwipeOptions = {
  viewport: HTMLElement;
  isEnabled?: () => boolean;
  getBaseOffset: () => number;
  onDrag: (offset: number) => void;
  onCommit: (direction: -1 | 0 | 1) => void;
};

export function initCarouselSwipe({
  viewport,
  isEnabled = () => true,
  getBaseOffset,
  onDrag,
  onCommit,
}: CarouselSwipeOptions): void {
  let startX = 0;
  let startY = 0;
  let baseOffset = 0;
  let isDragging = false;
  let directionLocked: 'horizontal' | 'vertical' | null = null;
  let capturedPointerId: number | null = null;

  viewport.style.touchAction = 'pan-y pinch-zoom';

  function releaseCapture(): void {
    if (capturedPointerId !== null && viewport.hasPointerCapture(capturedPointerId)) {
      viewport.releasePointerCapture(capturedPointerId);
    }

    capturedPointerId = null;
  }

  function resetGesture(): void {
    isDragging = false;
    directionLocked = null;
    releaseCapture();
  }

  viewport.addEventListener(
    'pointerdown',
    (event) => {
      if (!isEnabled() || event.button !== 0 || isInteractiveTarget(event.target)) return;

      isDragging = true;
      directionLocked = null;
      startX = event.clientX;
      startY = event.clientY;
      baseOffset = getBaseOffset();
      viewport.setPointerCapture(event.pointerId);
      capturedPointerId = event.pointerId;
    },
    { passive: true },
  );

  viewport.addEventListener(
    'pointermove',
    (event) => {
      if (!isDragging) return;

      const deltaX = startX - event.clientX;
      const deltaY = startY - event.clientY;

      if (!directionLocked) {
        if (Math.abs(deltaX) < DIRECTION_LOCK_THRESHOLD && Math.abs(deltaY) < DIRECTION_LOCK_THRESHOLD) {
          return;
        }

        directionLocked = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';

        if (directionLocked === 'vertical') {
          resetGesture();
          return;
        }
      }

      if (directionLocked !== 'horizontal') return;

      event.preventDefault();
      onDrag(Math.max(0, baseOffset + deltaX));
    },
    { passive: false },
  );

  const end = (event: PointerEvent): void => {
    if (!isDragging) return;

    const lockedDirection = directionLocked;
    resetGesture();

    if (!isEnabled()) {
      onCommit(0);
      return;
    }

    if (lockedDirection === 'vertical') {
      onCommit(0);
      return;
    }

    const deltaX = startX - event.clientX;
    const deltaY = startY - event.clientY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    const isHorizontalSwipe =
      lockedDirection === 'horizontal' ||
      (lockedDirection === null && absDeltaX >= SWIPE_THRESHOLD && absDeltaX > absDeltaY);

    if (isHorizontalSwipe && absDeltaX >= SWIPE_THRESHOLD) {
      onCommit(deltaX > 0 ? 1 : -1);
      return;
    }

    onCommit(0);
  };

  viewport.addEventListener('pointerup', end);
  viewport.addEventListener('pointercancel', end);
  viewport.addEventListener('lostpointercapture', resetGesture);
}
